import { useEffect, useState } from 'react';
import { CoachCard } from '../../design-system/concept/coach-card';
import { ActionButton } from '../../design-system/concept/action-button';
import { Field, ChoiceField } from '../../design-system/concept/field';
import { ageFromBirthDate, isCreatableAge, MIN_AGE, MAX_AGE } from '../../lib/age';
import type { ClientData } from '../../lib/interfaces';
import { hasFeature, noun } from '../../lib/features';

// Weight is a fitness reading: retail neither asks for nor shows it, and has
// no body fat bands for age to choose between.
const WEIGHT = hasFeature('weight');
const BODY_FAT = hasFeature('bodyFat');
import { weightFieldProblem } from '../../lib/scan/weight';

const asDateInput = (d?: Date | string) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};

/**
 * Edit a client's details.
 *
 * This did not exist. PUT /profiles/{id} wrote `height` and silently dropped
 * everything else, so a mistyped name or birth date could not be corrected
 * through any surface. That matters beyond tidiness: `age` is derived from
 * `birthDate` and drives the body-fat bands, so a wrong date is a wrong
 * clinical label with no way to fix it.
 */
export const ClientDetailsCard: React.FC<{
  client: ClientData;
  onSave: (patch: Partial<ClientData>) => Promise<unknown>;
}> = ({ client, onSave }) => {
  const initial = {
    firstName: client.firstName ?? '',
    lastName: client.lastName ?? '',
    email: client.email ?? '',
    birthDate: asDateInput(client.birthDate),
    height: client.height ? String(client.height) : '',
    weight: client.weight ? String(client.weight) : '',
    gender: (client.gender === 'female' ? 'female' : 'male') as 'male' | 'female',
  };

  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync when the client loads or changes underneath, but never while the
  // coach is mid-edit.
  useEffect(() => {
    if (!editing) setForm(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.ID, client.firstName, client.lastName, client.email, client.height, editing]);

  const set = (field: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const age = form.birthDate ? ageFromBirthDate(new Date(form.birthDate)) : null;

  const errors: Partial<Record<keyof typeof form, string>> = {};
  if (!form.firstName.trim()) errors.firstName = 'Required.';
  if (!form.lastName.trim()) errors.lastName = 'Required.';
  if (!form.email.trim()) errors.email = 'Required.';
  if (form.birthDate && age !== null && !isCreatableAge(age))
    errors.birthDate = `That date gives an age of ${age}; it must be between ${MIN_AGE} and ${MAX_AGE}.`;
  const weightProblem = weightFieldProblem(form.weight);
  if (weightProblem) errors.weight = weightProblem;
  const height = Number(form.height);
  if (form.height && (!Number.isFinite(height) || height < 100 || height > 250))
    errors.height = 'Between 100 and 250 cm.';
  const valid = Object.keys(errors).length === 0;

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        gender: form.gender,
        ...(form.birthDate ? { birthDate: new Date(form.birthDate) } : {}),
        ...(form.height ? { height: Number(form.height) } : {}),
        ...(form.weight ? { weight: Number(form.weight.replace(',', '.')) } : {}),
      } as Partial<ClientData>);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the details.');
    } finally {
      setSaving(false);
    }
  };

  const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <>
      <span className="text-xs text-foreground-soft">{label}</span>
      <span className="tabular text-xs font-semibold">{value || '—'}</span>
    </>
  );

  return (
    <CoachCard
      testId="client-details"
      title={`${noun.Client} details`}
      provenance={
        BODY_FAT
          ? `Age is derived from the birth date by the server, and sets which body-fat band this ${noun.client} is read against.`
          : 'Age is derived from the birth date by the server.'
      }
    >
      {editing ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="edit-firstName" label="First name" required value={form.firstName} onChange={set('firstName')} error={errors.firstName} />
            <Field id="edit-lastName" label="Last name" required value={form.lastName} onChange={set('lastName')} error={errors.lastName} />
          </div>
          <Field id="edit-email" label="Email" type="email" required value={form.email} onChange={set('email')} error={errors.email} />
          <div className={`grid gap-4 ${WEIGHT ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            <Field
              id="edit-birthDate"
              label="Birth date"
              type="date"
              value={form.birthDate}
              onChange={set('birthDate')}
              error={errors.birthDate}
              hint={age !== null && !errors.birthDate ? `${age} years old` : undefined}
            />
            <Field id="edit-height" label="Height (cm)" type="number" value={form.height} onChange={set('height')} error={errors.height} />
            {WEIGHT && (
              <Field id="edit-weight" label="Weight (kg)" type="number" value={form.weight} onChange={set('weight')} error={errors.weight} />
            )}
          </div>
          <ChoiceField
            label="Gender"
            value={form.gender}
            options={[
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
            ]}
            onChange={(v) => set('gender')(v)}
          />
          {error && <p className="text-[11px] text-destructive">{error}</p>}
          <div className="flex gap-2">
            <ActionButton variant="primary" onClick={save} disabled={saving || !valid} testId="client-details-save">
              {saving ? 'Saving…' : 'Save details'}
            </ActionButton>
            <ActionButton
              onClick={() => {
                setForm(initial);
                setEditing(false);
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </ActionButton>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
            <Row label="Name" value={`${client.firstName ?? ''} ${client.lastName ?? ''}`.trim()} />
            <Row label="Email" value={client.email} />
            <Row label="Birth date" value={initial.birthDate} />
            <Row label="Age" value={client.age ? `${client.age} y` : ''} />
            <Row label="Height" value={client.height ? `${client.height} cm` : ''} />
            <Row label="Gender" value={client.gender} />
          </div>
          {!initial.birthDate && (
            // The script cannot recompute an age without one; this is where
            // that gets fixed.
            <p className="text-[11px] text-[hsl(var(--projected))]">
              No birth date on file, so this client's age cannot be verified.
            </p>
          )}
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="client-details-edit"
            className="self-start text-[11px] font-medium text-primary hover:underline"
          >
            Edit details
          </button>
        </>
      )}
    </CoachCard>
  );
};
